import { Profile } from '~/helpers/userProfile';
import { colors } from '~/styles/colors';
import { ThermalComfortCountInfo, ThermalComfortUnitsInfo } from '../contexts/ThermalComfortDataContext';
import { ThermalComfortVariant } from '../ThermalComfort/components/UnitsInfo/types';
import { DayThermalComfortInfo, MonthThermalComfortInfo, ThermalComfortHistoryInfo } from '../ThermalComfort/types';
import { GetOverviewThermalComfort, GetThermalComfortHistory } from '../types';
import { ThermalComfortStatusFilter } from '~/providers/types/overview';

export function canViewThermalComfort(profile: Profile): boolean {
  return !!profile.manageAllClients;
}

export function computeCountsFromUnits(units: ThermalComfortUnitsInfo): ThermalComfortCountInfo {
  return units.reduce(
    (acc, unit) => {
      acc[unit.thermalComfort] = (acc[unit.thermalComfort] ?? 0) + 1;
      return acc;
    },
    {
      comfortable: 0, regular: 0, bad: 0, inoperative: 0,
    } as ThermalComfortCountInfo,
  );
}

export function deriveUnitStatus(
  unitIndex: number,
  countInoperantEnvironments: number,
): ThermalComfortVariant {
  if (unitIndex === 0 && countInoperantEnvironments >= 0) return 'inoperative';
  if (unitIndex > 7.0) return 'comfortable';
  if (unitIndex >= 5.0) return 'regular';
  return 'bad';
}

export function parseUnits(data: GetOverviewThermalComfort['UNITS']): ThermalComfortUnitsInfo {
  return data.map((item) => ({
    id: item.UNIT_ID,
    lat: item.UNIT_LAT ? Number(item.UNIT_LAT) : null,
    lon: item.UNIT_LON ? Number(item.UNIT_LON) : null,
    name: item.UNIT_NAME,
    state: item.STATE_NAME,
    city: item.CITY_NAME,
    cityId: item.CITY_ID,
    unitIndex: item.UNIT_INDEX,
    thermalComfort: deriveUnitStatus(
      item.UNIT_INDEX,
      item.COUNT_INOPERANT_ENVIRONMENTS,
    ),
    environments: {
      bad: item.COUNT_BAD_ENVIRONMENTS,
      comfortable: item.COUNT_COMFORTABLE_ENVIRONMENTS,
      regular: item.COUNT_REGULAR_ENVIRONMENTS,
      inoperative: item.COUNT_INOPERANT_ENVIRONMENTS,
      outsideOperatingHours: item.COUNT_OUTSIDE_OPERATING_HOURS_ENVIRONMENTS,
    },
  }));
}

export function statusFilterToVariant(status: ThermalComfortStatusFilter): ThermalComfortVariant {
  switch (status) {
    case 'bad': return 'bad';
    case 'comfortable': return 'comfortable';
    case 'regular': return 'regular';
    default: return 'inoperative';
  }
}

export function parseHistoryData(data: GetThermalComfortHistory): ThermalComfortHistoryInfo {
  return {
    TYPE: data.TYPE,
    HISTORY: [...data.HISTORY].reverse(),
  } as DayThermalComfortInfo | MonthThermalComfortInfo;
}

export function parseThermalComfortColor(thermalComfort: ThermalComfortVariant): string {
  switch (thermalComfort) {
    case 'comfortable': return colors.state.success.base;
    case 'regular': return colors.state.warning.base;
    case 'bad': return colors.state.danger.base;
    default: return colors.icon['soft-400'];
  }
}
